import { Suspense, useEffect, type ReactNode } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { ToastContainer } from "react-toastify";
import { jandiTheme } from "@/theme/jandiTheme";
import "@/styles/index.scss";
import "aos/dist/aos.css";
import "react-toastify/dist/ReactToastify.css";
import "@/styles/appToast.css";
import "@/i18n";
import { initGA } from "@/utils/analytics";
import RouteLoadingFallback from "@/shared/RouteLoadingFallback";
import { GTM_ID, META_PIXEL_ID } from "./documentSnippets";

function ensureMeta(name: string, content: string) {
  if (document.querySelector(`meta[name="${name}"]`)) return;
  const m = document.createElement("meta");
  m.setAttribute("name", name);
  m.setAttribute("content", content);
  document.head.appendChild(m);
}

function ensureHttpEquivMeta(httpEquiv: string, content: string) {
  if (document.querySelector(`meta[http-equiv="${httpEquiv}"]`)) return;
  const m = document.createElement("meta");
  m.setAttribute("http-equiv", httpEquiv);
  m.setAttribute("content", content);
  document.head.appendChild(m);
}

function ensureLink(rel: string, href: string, extra?: Record<string, string>) {
  const sel = `link[rel="${rel}"][href="${href}"]`;
  if (document.querySelector(sel)) return;
  const l = document.createElement("link");
  l.setAttribute("rel", rel);
  l.setAttribute("href", href);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => l.setAttribute(k, v));
  }
  document.head.appendChild(l);
}

function ensureStylesheet(href: string) {
  if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
  const l = document.createElement("link");
  l.setAttribute("rel", "stylesheet");
  l.setAttribute("href", href);
  document.head.appendChild(l);
}

function ensureGsiScript() {
  if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) return;
  const s = document.createElement("script");
  s.src = "https://accounts.google.com/gsi/client";
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

function ensureGtmScript() {
  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_ID}"]`)) return;
  const w = window as Window & { dataLayer?: Record<string, unknown>[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
}

function ensureMetaPixelScript() {
  type Fbq = {
    (...args: unknown[]): void;
    callMethod?: (...args: unknown[]) => void;
    queue: unknown[][];
    loaded: boolean;
    version: string;
    push: Fbq;
  };
  const w = window as unknown as Window & { fbq?: Fbq; _fbq?: Fbq };
  if (w.fbq) return;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  w.fbq = fbq;
  w._fbq = fbq;

  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  fbq("init", META_PIXEL_ID);
  fbq("track", "PageView");
}

function registerFirebaseMessagingSw() {
  if (!("serviceWorker" in navigator)) return;
  const onLoad = () => {
    navigator.serviceWorker
      .register("/firebase-messaging-sw.js")
      .then((registration) => {
        console.log("Firebase Messaging Service Worker registered:", registration);
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  };
  if (document.readyState === "complete") onLoad();
  else window.addEventListener("load", onLoad);
}

/**
 * Client-only parity with the old index.html head for CSR routes (and as fallback after hydration).
 */
function useClientDocumentEnhancements() {
  useEffect(() => {
    (window as Window & { __JANDI_CLIENT_BOOTED__?: boolean }).__JANDI_CLIENT_BOOTED__ = true;
    document.documentElement.setAttribute("data-jandi-client", "ready");
    try {
      window.sessionStorage.removeItem("jandi-client-error");
      window.sessionStorage.removeItem("jandi-preload-reload");
    } catch {
      // Storage can be unavailable in privacy-restricted WebViews.
    }

    initGA();
    void import("bootstrap");

    ensureMeta(
      "viewport",
      "width=device-width, initial-scale=1, viewport-fit=cover"
    );
    ensureMeta("author", "Jandi");
    ensureMeta("theme-color", "#4285F4");
    ensureMeta("apple-mobile-web-app-capable", "yes");
    ensureMeta("ibthemes", "ATFN");
    ensureHttpEquivMeta("x-ua-compatible", "ie=edge");
    ensureHttpEquivMeta(
      "Content-Security-Policy",
      "default-src * data: blob:; script-src * 'unsafe-inline' 'unsafe-eval' data: blob:; style-src * 'unsafe-inline' data: blob:; img-src * data: blob:; connect-src * data: blob:; frame-ancestors *;"
    );

    // Drop obsolete keywords if present from older shells.
    document.querySelectorAll('meta[name="keywords"]').forEach((node) => node.remove());
    if (document.documentElement) {
      document.documentElement.lang = "es-PR";
    }

    ensureLink("preconnect", "https://fonts.cdnfonts.com", { crossorigin: "anonymous" });
    ensureStylesheet("https://fonts.cdnfonts.com/css/object-sans");
    ensureLink("manifest", "/manifest.json");
    registerFirebaseMessagingSw();

    // Start third-party scripts only after React has hydrated. Meta's iOS
    // in-app browser injects its own script when Pixel is present; starting
    // these from SSR <head> can mutate/compete with the DOM before hydration.
    const thirdPartyTimer = window.setTimeout(() => {
      ensureGtmScript();
      ensureMetaPixelScript();
      ensureGsiScript();
    }, 0);

    const timer = window.setTimeout(() => {
      const ua = navigator.userAgent || navigator.vendor || (window as unknown as { opera?: string }).opera || "";
      const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
      const isInApp = /Instagram|FBAN|FBAV|FBIOS|FB_IAB|Line\/|Twitter|TikTok|Bytedance|Snapchat|MicroMessenger|LinkedInApp/i.test(ua);
      const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua) && !isInApp;
      const isWebView = isIOSDevice && (!isSafari || isInApp);
      if (!isWebView) return;
      requestAnimationFrame(() => {
        // Avoid fighting `transform: none !important` on body — poke the root instead
        const root = document.getElementById("root");
        if (root) {
          root.style.webkitTransform = "translateZ(0)";
          root.style.transform = "translateZ(0)";
          void root.offsetHeight;
        }
        window.scrollTo(0, 1);
        window.scrollTo(0, 0);
        window.dispatchEvent(new Event("resize"));
      });
    }, 400);
    return () => {
      window.clearTimeout(thirdPartyTimer);
      window.clearTimeout(timer);
    };
  }, []);
}

export default function VikeRootLayout({ children }: { children: ReactNode }) {
  useClientDocumentEnhancements();

  return (
    <ThemeProvider theme={jandiTheme}>
      <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="app-toast"
        bodyClassName="app-toast__body"
        progressClassName="app-toast__progress"
      />
    </ThemeProvider>
  );
}
